import { Play } from "lucide-react";
import { useId } from "react";

export type MockMediaScene = "workshop" | "group" | "chart" | "office" | "campus" | "handshake" | "presentation" | "coffee";

interface SceneGradient {
	from: string;
	to: string;
}

const SCENE_GRADIENT: Record<MockMediaScene, SceneGradient> = {
	workshop: { from: "#dbeafe", to: "#93c5fd" },
	group: { from: "#fde68a", to: "#f97316" },
	chart: { from: "#1e293b", to: "#0f172a" },
	office: { from: "#e0f2fe", to: "#bae6fd" },
	campus: { from: "#bfdbfe", to: "#dbeafe" },
	handshake: { from: "#ede9fe", to: "#c4b5fd" },
	presentation: { from: "#312e81", to: "#4338ca" },
	coffee: { from: "#fef3c7", to: "#d6b48a" },
};

interface FigureProps {
	x: number;
	y: number;
	scale?: number;
	shirt: string;
	skin: string;
	hair?: string;
}

function Figure({ x, y, scale = 1, shirt, skin, hair = "#1f2937" }: FigureProps) {
	return (
		<g transform={`translate(${x} ${y}) scale(${scale})`}>
			<path d="M-14 40c0-12 6-18 14-18s14 6 14 18z" fill={shirt} />
			<circle cx="0" cy="10" r="9" fill={skin} />
			<path d="M-9 10a9 9 0 0 1 18 0c-2-3-5-5-9-5s-7 2-9 5z" fill={hair} />
		</g>
	);
}

function SceneShapes({ scene }: { scene: MockMediaScene }) {
	switch (scene) {
		case "workshop":
			return (
				<>
					<rect x="26" y="14" width="108" height="48" rx="3" fill="#fff" opacity="0.95" />
					<rect x="36" y="24" width="52" height="3" rx="1.5" fill="#94a3b8" />
					<rect x="36" y="32" width="70" height="3" rx="1.5" fill="#cbd5e1" />
					<rect x="36" y="40" width="40" height="3" rx="1.5" fill="#cbd5e1" />
					<rect x="98" y="24" width="26" height="26" rx="2" fill="#60a5fa" opacity="0.6" />
					<Figure x={48} y={58} scale={0.9} shirt="#2563eb" skin="#c68642" />
					<Figure x={82} y={62} scale={0.95} shirt="#10b981" skin="#f1c27d" hair="#4a2c17" />
					<Figure x={118} y={60} scale={0.9} shirt="#f59e0b" skin="#8d5524" />
				</>
			);
		case "group":
			return (
				<>
					<ellipse cx="80" cy="96" rx="90" ry="14" fill="#7c2d12" opacity="0.25" />
					<Figure x={22} y={52} scale={0.95} shirt="#0ea5e9" skin="#a0673f" />
					<Figure x={50} y={44} scale={1.05} shirt="#ef4444" skin="#f5d0b0" hair="#c94f2e" />
					<Figure x={80} y={40} scale={1.1} shirt="#8b5cf6" skin="#7a4a2a" />
					<Figure x={110} y={44} scale={1.05} shirt="#14b8a6" skin="#ffdbac" hair="#b5651d" />
					<Figure x={138} y={52} scale={0.95} shirt="#f59e0b" skin="#c68642" />
				</>
			);
		case "chart":
			return (
				<>
					<rect x="18" y="14" width="124" height="72" rx="6" fill="#0b1220" opacity="0.6" />
					<g stroke="#334155" strokeWidth="1">
						<path d="M30 30h100M30 46h100M30 62h100" />
					</g>
					<rect x="36" y="56" width="10" height="20" rx="2" fill="#3b82f6" />
					<rect x="52" y="46" width="10" height="30" rx="2" fill="#a855f7" />
					<rect x="68" y="50" width="10" height="26" rx="2" fill="#3b82f6" />
					<rect x="84" y="38" width="10" height="38" rx="2" fill="#a855f7" />
					<rect x="100" y="30" width="10" height="46" rx="2" fill="#3b82f6" />
					<rect x="116" y="24" width="10" height="52" rx="2" fill="#a855f7" />
					<path d="M38 52 L57 44 L73 46 L89 36 L105 28 L121 20" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" />
				</>
			);
		case "office":
			return (
				<>
					<rect x="0" y="0" width="160" height="60" fill="#fff" opacity="0.4" />
					<g fill="#fff" opacity="0.8">
						<rect x="12" y="8" width="30" height="40" rx="2" />
						<rect x="48" y="8" width="30" height="40" rx="2" />
						<rect x="84" y="8" width="30" height="40" rx="2" />
						<rect x="120" y="8" width="30" height="40" rx="2" />
					</g>
					<rect x="0" y="62" width="160" height="38" fill="#e2e8f0" />
					<rect x="30" y="60" width="100" height="6" rx="2" fill="#94a3b8" />
					<rect x="60" y="44" width="28" height="18" rx="2" fill="#1e293b" />
					<rect x="63" y="47" width="22" height="12" rx="1" fill="#3b82f6" opacity="0.7" />
					<circle cx="130" cy="52" r="8" fill="#22c55e" />
					<rect x="128" y="58" width="4" height="8" fill="#15803d" />
					<Figure x={40} y={40} scale={0.85} shirt="#6366f1" skin="#e0ac69" />
				</>
			);
		case "campus":
			return (
				<>
					<circle cx="130" cy="20" r="10" fill="#fde68a" />
					<rect x="0" y="70" width="160" height="30" fill="#86efac" />
					<rect x="40" y="34" width="80" height="40" fill="#f8fafc" />
					<path d="M34 36 L80 16 L126 36 Z" fill="#cbd5e1" />
					<g fill="#cbd5e1">
						<rect x="48" y="44" width="6" height="30" />
						<rect x="64" y="44" width="6" height="30" />
						<rect x="90" y="44" width="6" height="30" />
						<rect x="106" y="44" width="6" height="30" />
					</g>
					<rect x="75" y="54" width="10" height="20" fill="#1e3a8a" />
					<circle cx="18" cy="60" r="12" fill="#22c55e" />
					<rect x="16" y="66" width="4" height="12" fill="#92400e" />
					<circle cx="144" cy="58" r="12" fill="#16a34a" />
					<rect x="142" y="64" width="4" height="12" fill="#92400e" />
				</>
			);
		case "handshake":
			return (
				<>
					<ellipse cx="80" cy="98" rx="70" ry="12" fill="#4c1d95" opacity="0.2" />
					<Figure x={50} y={36} scale={1.15} shirt="#3b82f6" skin="#c68642" />
					<Figure x={110} y={36} scale={1.15} shirt="#f97316" skin="#f5d0b0" hair="#4a2c17" />
					<path d="M60 74c8-4 16-6 20-6s12 2 20 6" fill="none" stroke="#c68642" strokeWidth="6" strokeLinecap="round" />
					<path d="M80 68c4 0 8 1 12 3" fill="none" stroke="#f5d0b0" strokeWidth="6" strokeLinecap="round" />
				</>
			);
		case "presentation":
			return (
				<>
					<rect x="28" y="10" width="104" height="54" rx="3" fill="#f8fafc" />
					<rect x="40" y="46" width="12" height="12" fill="#3b82f6" />
					<rect x="58" y="36" width="12" height="22" fill="#a855f7" />
					<rect x="76" y="28" width="12" height="30" fill="#3b82f6" />
					<rect x="94" y="20" width="12" height="38" fill="#22c55e" />
					<rect x="112" y="30" width="8" height="28" fill="#f59e0b" />
					<rect x="0" y="80" width="160" height="20" fill="#1e1b4b" />
					<g fill="#312e81">
						<circle cx="20" cy="86" r="7" />
						<circle cx="44" cy="88" r="7" />
						<circle cx="68" cy="86" r="7" />
						<circle cx="92" cy="88" r="7" />
						<circle cx="116" cy="86" r="7" />
						<circle cx="140" cy="88" r="7" />
					</g>
				</>
			);
		case "coffee":
			return (
				<>
					<rect x="20" y="22" width="70" height="52" rx="4" fill="#fff" />
					<g stroke="#cbd5e1" strokeWidth="2" strokeLinecap="round">
						<path d="M30 36h40M30 46h50M30 56h34" />
					</g>
					<circle cx="122" cy="40" r="16" fill="#fff" />
					<circle cx="122" cy="40" r="11" fill="#6b3d1f" />
					<path d="M138 34c6 0 6 12 0 12" fill="none" stroke="#fff" strokeWidth="4" />
					<circle cx="112" cy="78" r="12" fill="#fff" />
					<circle cx="112" cy="78" r="8" fill="#a16207" />
					<rect x="30" y="80" width="24" height="6" rx="3" fill="#0ea5e9" />
				</>
			);
	}
}

interface MockPhotoProps {
	scene: MockMediaScene;
	className?: string;
	caption?: string;
	rounded?: string;
}

export function MockPhoto({ scene, className = "", caption, rounded = "rounded-lg" }: MockPhotoProps) {
	const gradient = SCENE_GRADIENT[scene];
	const gradientId = useId();
	return (
		<div className={`relative overflow-hidden ${rounded} ${className}`} aria-hidden="true">
			<svg viewBox="0 0 160 100" preserveAspectRatio="xMidYMid slice" className="block h-full w-full" aria-hidden="true">
				<defs>
					<linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
						<stop offset="0" stopColor={gradient.from} />
						<stop offset="1" stopColor={gradient.to} />
					</linearGradient>
				</defs>
				<rect width="160" height="100" fill={`url(#${gradientId})`} />
				<SceneShapes scene={scene} />
			</svg>
			{caption ? (
				<div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent px-2 pb-1.5 pt-4 text-[10px] font-medium text-white">
					{caption}
				</div>
			) : null}
		</div>
	);
}

interface MockVideoThumbProps {
	scene: MockMediaScene;
	duration: string;
	className?: string;
	playSize?: number;
	rounded?: string;
}

export function MockVideoThumb({ scene, duration, className = "", playSize = 40, rounded = "rounded-lg" }: MockVideoThumbProps) {
	return (
		<div className={`relative overflow-hidden ${rounded} ${className}`} aria-hidden="true">
			<MockPhoto scene={scene} className="h-full w-full" rounded="rounded-none" />
			<div className="absolute inset-0 bg-black/30" />
			<span
				className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white"
				style={{ width: playSize, height: playSize }}
			>
				<Play style={{ width: playSize * 0.5, height: playSize * 0.5 }} fill="currentColor" />
			</span>
			<span className="absolute bottom-1.5 right-1.5 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-semibold text-white">
				{duration}
			</span>
		</div>
	);
}
